import { Injectable } from '@angular/core'
import { ActivatedRouteSnapshot, Router } from '@angular/router'
import { PageContextIds, PageContextProvider } from '../models/page-context.model'

/**
 * The candidate routes carry the published mirror id (`<contentId>_en-US`), but the backend's job tools
 * address a post by its content id and resolve the mirror server-side — a mirror id answers not-found.
 * Content ids are 20-char alphanumeric auto-ids (never underscored), so stripping a trailing locale
 * token cannot corrupt a real id.
 */
const stripMirrorSuffix = (id: string): string => id.replace(/_[a-z]{2}(-[A-Z]{2})?$/, '')

/**
 * Stateless resolver for the assistant's current-page context. Every send recomputes from live router
 * state, so no id from a prior screen leaks: the service holds only a Set of registered providers, never
 * any resolved id.
 *
 * Two contribution sources are merged:
 *  1. Route tree — each activated route may declare a `data.pageContext` map of
 *     `Record<paramToken, keyof PageContextIds>`; the resolver reads `routeConfig.data` (NOT the
 *     inheritance-merged `snapshot.data`, so a child never inherits a parent's mapping) and pulls each
 *     token from the node's `paramMap` then `queryParamMap`. This keeps the resolver fully generic — no
 *     per-route branching lives here.
 *  2. Providers — state-resident components that register a `PageContextProvider` for ids not present on
 *     the route (e.g. an id held only in component state).
 *
 * Route contributions win on a field collision; a provider whose `getPageContextIds()` throws is skipped.
 */
@Injectable({ providedIn: 'root' })
export class PageContextService {
  private providers = new Set<PageContextProvider>()

  constructor(private router: Router) {}

  /** Register a state-resident provider (call on component init). */
  register(provider: PageContextProvider): void {
    this.providers.add(provider)
  }

  /** Unregister a provider (call on component destroy) so its ids never leak past its screen. */
  unregister(provider: PageContextProvider): void {
    this.providers.delete(provider)
  }

  /**
   * Recompute the current page-context ids from live state. BFS-walks the activated route snapshot from
   * the root, collecting declared token→field mappings (deeper node overwrites), then merges registered
   * providers with route values winning on collision. Returns only defined fields.
   */
  resolve(): PageContextIds {
    const ids: Record<string, string> = {}
    // Defensive: this runs inline while building the send body (before the send's error pipe exists), so
    // an unexpected throw must never escape and break the message send — degrade to whatever was collected.
    try {
      const queue: ActivatedRouteSnapshot[] = [this.router.routerState.snapshot.root]
      while (queue.length) {
        const node = queue.shift()!
        const map = node.routeConfig?.data?.['pageContext'] as Record<string, keyof PageContextIds> | undefined
        if (map) {
          for (const [token, field] of Object.entries(map)) {
            const value = node.paramMap.get(token) ?? node.queryParamMap.get(token)
            if (value) ids[field] = field === 'currentJobPostId' ? stripMirrorSuffix(value) : value
          }
        }
        queue.push(...node.children)
      }
    } catch {
      // keep whatever route ids were collected before the failure
    }
    for (const provider of this.providers) {
      try {
        for (const [field, value] of Object.entries(provider.getPageContextIds())) {
          // Route values win on collision (skip a field already set); falsy provider values are dropped so
          // the payload never carries an empty/undefined id.
          if (value && !(field in ids)) ids[field] = value
        }
      } catch {
        // a throwing provider is swallowed so it cannot break the send; keep the rest
      }
    }
    return ids
  }
}
